import React, {useEffect, useState} from "react";
import {type UserGroup, type UserType} from "../types";
import TextareaAutosize from "react-textarea-autosize";
import {defaultUserGroupDescription} from "../constants";
import "./AddUserGroup.scss";
import {addGroupIdValidation, userGroupAlreadyExists, userGroupIdShouldBeNumber} from "./constants";
import {isUserGroupIdValid, toInteger} from "./helpers";

interface AddUserGroupProps {
  userTypes: UserType[];
  userGroups: UserGroup[];
  newUserGroups: UserGroup[];
  setNewUserGroups: (userGroups: UserGroup[]) => void;
  isDirty?: boolean;
  setIsDirty?: (isDirty: boolean) => void;
}

export const AddUserGroup = ({
  userTypes,
  userGroups,
  newUserGroups,
  setNewUserGroups,
  isDirty,
  setIsDirty
}: AddUserGroupProps) => {
  const [temporaryCode, setTemporaryCode] = useState<string>(undefined);
  const [validation, setValidation] = useState<string>(undefined);

  const resetValidation = () => {
    validation && setValidation(undefined);
  };

  const setDuplicateValidation = () => {
    setValidation(userGroupAlreadyExists);
  };

  const codeAlreadyExists = (code: number) =>
    [...userGroups, ...newUserGroups].find(userGroup => userGroup.code === code);

  const addNewUserGroup = () => {
    if (isEmptyString(temporaryCode)) {
      setValidation(addGroupIdValidation);
      return;
    }

    if (!isUserGroupIdValid(temporaryCode)) {
      setValidation(userGroupIdShouldBeNumber);
      return;
    }

    const code = toInteger(temporaryCode);

    if (codeAlreadyExists(code)) {
      setDuplicateValidation();
      return;
    }

    setTemporaryCode(undefined);
    resetValidation();

    setNewUserGroups([
      ...newUserGroups,
      {
        code,
        description: defaultUserGroupDescription(code)
      }
    ]);

    isDirty && setIsDirty?.(false);
  };

  const isEmptyString = (value: string): boolean => value === undefined || value?.trim() === "";

  useEffect(() => {
    setTemporaryCode(undefined);
    resetValidation();
  }, [newUserGroups]);

  const onNewGroupIdInput: React.ChangeEventHandler<HTMLTextAreaElement> = event => {
    const code = event.target.value;

    setTemporaryCode(code);

    resetValidation();

    isEmptyString(code) ? isDirty && setIsDirty?.(false) : !isDirty && setIsDirty?.(true);
  };

  return (
    <tr className="stable-row">
      <td className="left-column add-group__container">
        <div className="add-group__wrapper">
          <div className="add-group__input-wrapper">
            <TextareaAutosize
              onChange={onNewGroupIdInput}
              inputMode="numeric"
              value={temporaryCode ?? ""}
              id="id"
              className={`form-control required ${validation ? "is-invalid" : ""}`}
              placeholder="Add user group by ID"
            />
            {validation && <div className="invalid-feedback">{validation}</div>}
          </div>
          <button
            type="button"
            onClick={addNewUserGroup}
            className="btn btn-secondary btn-dark add-group__add-button">
            Add
          </button>
        </div>
      </td>
      {userTypes.map(userType => {
        return (
          <td key={userType.code} className="regular-column">
            <div className="form-group custom-control custom-checkbox small applicability-checkbox">
              <input type="checkbox" className="custom-control-input" />
            </div>
          </td>
        );
      })}
    </tr>
  );
};
